'use client';

import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { useGeolocation } from '@/shared/lib/hooks/useGeolocation';
import { Address } from '@/shared/model/Address';
import { VenuesQuery } from '../api/queries/VenuesQuery';
import { CompleteVenueWithGeoData } from '../types/CompleteVenueWithGeoData';
import { getDeliverableVenues } from '../utils/getDeliverableVenues';
import { getSortedVenuesByDistance } from '../utils/getSortedVenuesByDistance';
import { useGetVenueHrefAttributes } from './useGetVenueHrefAttributes';

export type FormValues = {
    deliveryType: DeliveryType;
    address: Address | null;
    isManual: boolean;
    selectedVenue: CompleteVenueWithGeoData | null;
    deliveryVenues: CompleteVenueWithGeoData[];
    pickupVenues: CompleteVenueWithGeoData[];
};

type Options = {
    initialDeliveryType?: DeliveryType;
    initialAddress?: Address | null;
    initialIsManual?: boolean;
    autoRedirect?: boolean;
    autoRedirectDelayMs?: number;
    checkPosition?: boolean;
    showAllDeliveryVenuesWithoutDeliverySelected?: boolean;
};

/**
 * Manages venue-selection form state, including delivery-area filtering and pickup sorting by location.
 */
export const useVenueSelectFilters = (options: Options = {}) => {
    const {
        initialDeliveryType = DeliveryType.DELIVERY,
        initialAddress = null,
        initialIsManual = true,
        autoRedirect = false,
        autoRedirectDelayMs = 1_000,
        checkPosition = true,
        showAllDeliveryVenuesWithoutDeliverySelected = false,
    } = options;

    const config = useContext(AppConfigContext);
    const getVenueHrefAttributes = useGetVenueHrefAttributes();

    const venuesQuery = useQuery(VenuesQuery);
    const venues: CompleteVenueWithGeoData[] = useMemo(
        () => (venuesQuery.data ?? [])?.map((venue) => ({ ...venue, distance: null })),
        [venuesQuery.data],
    );
    const { latitude, longitude, getCurrentPosition } = useGeolocation();

    const pickupVenues = useMemo(
        () => venues.filter((v) => v.deliveryMethods.some((d) => (d.areas?.length ?? 0) === 0)),
        [venues],
    );

    const isDeliveryAvailable = useMemo(
        () => getDeliverableVenues(venues, null, undefined, false).length !== 0,
        [venues],
    );

    const isPickupAvailable = useMemo(() => pickupVenues.length !== 0, [pickupVenues]);

    const formMethods = useForm<FormValues>({
        defaultValues: {
            deliveryType:
                (initialDeliveryType === DeliveryType.DELIVERY && isDeliveryAvailable) ||
                (initialDeliveryType === DeliveryType.PICKUP && isPickupAvailable)
                    ? initialDeliveryType
                    : isDeliveryAvailable
                      ? DeliveryType.DELIVERY
                      : DeliveryType.PICKUP,
            address: initialAddress,
            isManual: initialIsManual,
            selectedVenue: null,
            deliveryVenues: getDeliverableVenues(
                venues,
                initialAddress,
                { lat: latitude, lon: longitude },
                !showAllDeliveryVenuesWithoutDeliverySelected,
            ),
            pickupVenues,
        },
    });

    const handleChangeDeliveryType = async (deliveryType: DeliveryType) => {
        formMethods.setValue('deliveryType', deliveryType);
        if (
            checkPosition &&
            deliveryType === DeliveryType.PICKUP &&
            venues?.length &&
            (latitude === null || longitude === null)
        ) {
            const location = await getCurrentPosition();
            if (!location) {
                return;
            }
            const sortedVenues = getSortedVenuesByDistance(venues, {
                lat: location.coords.latitude,
                lon: location.coords.longitude,
            });
            formMethods.setValue('pickupVenues', sortedVenues);
        }
    };

    const handleChangeAddress = useCallback(
        (address: Address | Record<string, never> | null) => {
            if (!venues?.length) {
                return;
            }
            const filteredVenues = getDeliverableVenues(
                venues,
                address,
                { lat: latitude, lon: longitude },
                !showAllDeliveryVenuesWithoutDeliverySelected,
            );

            if (autoRedirect && filteredVenues.length === 1) {
                const venue = filteredVenues[0];
                formMethods.setValue('selectedVenue', venue);
                setTimeout(() => {
                    const { href, onClick } = getVenueHrefAttributes(venue.apiPartnerSettingsId, {
                        address: address?.street ? (address as Address) : null,
                        deliveryType: formMethods.getValues('deliveryType'),
                    });
                    if (config.apiPartnerSettingsId === venue.apiPartnerSettingsId) {
                        onClick();
                    } else {
                        window.location.href = href;
                    }
                }, autoRedirectDelayMs);
                return;
            }
            formMethods.setValue('deliveryVenues', filteredVenues);
        },
        [
            autoRedirectDelayMs,
            formMethods,
            autoRedirect,
            venues,
            getVenueHrefAttributes,
            config.apiPartnerSettingsId,
            latitude,
            longitude,
            showAllDeliveryVenuesWithoutDeliverySelected,
        ],
    );

    return {
        formMethods,
        handleChangeDeliveryType,
        handleChangeAddress,
        isDeliveryAvailable,
        isPickupAvailable,
    };
};
